import React, { useCallback, useEffect, useRef, useState } from 'react';

import Spinner from '@atlaskit/spinner';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient';

import { useInitialUrl } from '../hooks/useInitialUrl';
import { DatabaseValueContainerContentUnifiedQuery } from '../DatabaseValueContainerContentUnifiedQuery.graphql';
import type { DatabaseValueContainerContentUnifiedQuery as DatabaseValueContainerContentUnifiedQueryType } from '../__types__/DatabaseValueContainerContentUnifiedQuery.ts';

type NullableString = string | null | undefined;

type Identity = {
	accountId: NullableString;
	nickname: NullableString;
	picture: NullableString;
	email: NullableString;
	locale: NullableString;
};
type Content = {
	contentReferenceId: NullableString;
	spaceId: NullableString;
	spaceKey: NullableString;
	spaceName: NullableString;
};

export type DatabaseValueContainerProps = {
	contentId: string;
	entryId: string;
	fieldId: string;
	isEdit?: boolean;
};

export const DatabaseValueContainer = React.memo(
	({ contentId, entryId, fieldId, isEdit }: DatabaseValueContainerProps) => {
		const iframeRef = useRef<HTMLIFrameElement>(null);
		const [content, setContent] = useState<Content | null>(null);
		const [dataLoaded, setDataLoaded] = useState(false);

		const [width, setWidth] = useState(0);
		const [height, setHeight] = useState(0);

		const databaseUrl = new URL(useInitialUrl(), window.location.origin);

		const [identity, setIdentity] = useState<Identity | null>(null);
		const { activationId, userId, cloudId } = useSessionData();

		useEffect(() => {
			// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
			getApolloClient()
				.query({
					query: DatabaseValueContainerContentUnifiedQuery,
					variables: { contentId },
				})
				.then(({ data }: { data: DatabaseValueContainerContentUnifiedQueryType }) => {
					setContent({
						contentReferenceId: data.content?.nodes?.[0]?.referenceId,
						spaceId: data.content?.nodes?.[0]?.space?.id,
						spaceKey: data.content?.nodes?.[0]?.space?.key,
						spaceName: data.content?.nodes?.[0]?.space?.name,
					});
					setIdentity({
						accountId: data?.user?.id,
						nickname: data?.user?.displayName,
						picture: data?.user?.photos?.find((photo) => photo?.isPrimary)?.value,
						email: data?.user?.emails?.find((email) => email?.isPrimary)?.value,
						locale: data?.user?.locale,
					});
					setDataLoaded(true);
				});
		}, [contentId]);

		const bridgeData = {
			cloudId,
			activationId,
			userId,
			identity,
			contentId,
			...(content ? { content } : {}),
		};

		const messageCallback = useCallback((event: MessageEvent) => {
			const isFromExpectedIframe =
				iframeRef.current && event.source === iframeRef.current.contentWindow;

			if (isFromExpectedIframe) {
				try {
					const data = JSON.parse(event.data);
					if (data.method === 'resize' && 'width' in data && 'height' in data) {
						setWidth(data.width);
						setHeight(data.height);
					}
				} catch (error) {}
			}
		}, []);

		useEffect(() => {
			window.addEventListener('message', messageCallback);

			return () => {
				window.removeEventListener('message', messageCallback);
			};
		}, [messageCallback]);

		if (entryId && fieldId && contentId) {
			databaseUrl.searchParams.delete('referencing');
			databaseUrl.searchParams.set('contentId', contentId);
			databaseUrl.searchParams.set('entryId', entryId);
			databaseUrl.searchParams.set('fieldId', fieldId);
			return (
				<>
					<iframe
						style={{
							width: `${width}px`,
							height: `${height}px`,
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							border: '0px',
							pointerEvents: isEdit ? 'none' : 'auto',
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							verticalAlign: 'middle',
						}}
						src={databaseUrl.toString()}
						{...(dataLoaded ? { 'data-bridge-data': JSON.stringify(bridgeData) } : {})}
						title="database-value-frame"
						ref={iframeRef}
					/>
					{width === 0 || height === 0 ? <Spinner testId="database-value-spinner" /> : null}
				</>
			);
		}

		return null;
	},
);
